import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  assertNever,
  AskBasis,
  FEC_WORK_LINE_CAPTION,
  ProspectMatchStatus,
  type AcceptedRecordDto,
  type CallSheetMembershipDto,
  type ProspectCallBookDto,
  type ProspectDetailDto as Detail,
  type ProspectDto,
  type ProspectWorkLineDto,
  type UpdateProspectRequest,
} from '@signalvote/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatPhone } from '@/lib/phone';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAddToCallSheet,
  useCreateCallSheet,
  useProspectDetail,
  useRecordMatchVerdict,
  useRemoveFromCallSheet,
  useUpdateProspect,
  useUpdateProspectNotes,
} from '@/hooks/queries';
import {
  addressFromRecord,
  addressInUse,
  prospectAddress,
  recordAddress,
  recordWork,
  unlinkVerdict,
  workFromRecord,
  workInUse,
} from '@/lib/fundraising/prospectRecord';
import { candidateKey } from '@/lib/fundraising/matchVerdict';
import { cn } from '@/lib/utils';
import { shortDay } from './activityDay';
import { AddToCallSheetDialog } from './AddToCallSheetDialog';
import { ProspectDetailsForm } from './ProspectDetailsForm';
import { ProspectNoteEditor } from './ProspectNoteEditor';
import { ProspectRemove } from './ProspectRemove';
import { ProspectTimeline } from './ProspectTimeline';

// Asks, commitments and federal gifts as whole dollars: "$5,800".
const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const count = new Intl.NumberFormat('en-US');
const plural = (n: number, one: string, many = `${one}s`) => `${count.format(n)} ${n === 1 ? one : many}`;
const joined = (separator: string, ...parts: (string | false | null | undefined)[]) =>
  parts.filter((part): part is string => !!part).join(separator);

// Each accepted record's letter on the page.
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const letterOf = (index: number) => LETTERS[index] ?? String(index + 1);

function askBasisLabel(basis: AskBasis): string {
  switch (basis) {
    case AskBasis.FederalLargestGift:
      return 'Their largest federal gift';
    default:
      return assertNever(basis);
  }
}

/** What the Suggested ask says when there's no Ask yet. */
function noAskCaption(status: ProspectMatchStatus): string {
  switch (status) {
    case ProspectMatchStatus.PossibleMatch:
      return 'Confirm a possible record to size it';
    case ProspectMatchStatus.NotMatchedYet:
      return 'After matching';
    case ProspectMatchStatus.NoneFound:
    case ProspectMatchStatus.FederalGiving:
      return 'No federal giving to size it';
    default:
      return assertNever(status);
  }
}

/** What Federal giving says when nothing is accepted. */
function noFederalCaption(status: ProspectMatchStatus): string {
  switch (status) {
    case ProspectMatchStatus.PossibleMatch:
      return 'None confirmed yet';
    case ProspectMatchStatus.NotMatchedYet:
      return 'Not matched yet';
    case ProspectMatchStatus.NoneFound:
    case ProspectMatchStatus.FederalGiving:
      return 'None found';
    default:
      return assertNever(status);
  }
}

const NAME_CLASS = 'text-[22px] font-bold leading-7 tracking-tight text-foreground';

export interface ProspectProfileProps {
  prospectId: string;
  /** Every call book with everyone on it, from the book read — "Add to call book" ticks from these, and a
   *  removal from a book sends its freshest token. */
  sheets: CallSheetMembershipDto[];
  /** They were removed: leave wherever they were shown. */
  onRemoved: () => void;
  /** Their name as the profile's heading. The page's is its h1; a dialog names itself with it instead. */
  renderName?: (name: string, className: string) => ReactNode;
}

/**
 * One Prospect's whole profile — the one place their money, note, details, call books, federal records and
 * timeline are shown and changed, whether on their own page or opened over the Prospects book or a call
 * book. It reads the person by id, so it works the same wherever it opens. Correcting and removing them,
 * their note, taking them off a book, and — through Match review — the questions still open about them all
 * live here. Federal records can be unlinked (a "not them" verdict, after saying what the Ask becomes) or
 * copied into the person's details through the prospect PUT.
 */
export function ProspectProfile({ prospectId, sheets, onRemoved, renderName = (name, className) => <h1 className={className}>{name}</h1> }: ProspectProfileProps) {
  const { data, isLoading, isError } = useProspectDetail(prospectId);
  // Only ever this person's profile: stepping to someone not read yet shows the placeholder, never the last
  // person's details under the new name.
  const detail = data?.row.prospect.id === prospectId ? data : undefined;
  const waiting = !detail && !isError && (isLoading || !!data);
  return (
    <>
      {waiting && (
        <div data-testid="profile-loading" aria-busy="true" className="space-y-3">
          <span className="sr-only">Loading…</span>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}
      {isError && !isLoading && (
        <p className="text-sm text-muted-foreground">We couldn’t load this person. They may have been removed.</p>
      )}
      {detail && <Person key={detail.row.prospect.id} detail={detail} sheets={sheets} onRemoved={onRemoved} renderName={renderName} />}
    </>
  );
}

function Person({
  detail,
  sheets,
  onRemoved,
  renderName,
}: {
  detail: Detail;
  sheets: CallSheetMembershipDto[];
  onRemoved: () => void;
  renderName: NonNullable<ProspectProfileProps['renderName']>;
}) {
  const { row } = detail;
  const { prospect } = row;
  const [adding, setAdding] = useState(false);
  const { mutate: addToSheet, error: addError, reset: resetAdd } = useAddToCallSheet();
  const { mutate: createSheet, error: createError, reset: resetCreate } = useCreateCallSheet();
  const { mutate: update, isPending: isSaving, error: saveError, reset: resetSave } = useUpdateProspect();
  const save = (request: UpdateProspectRequest, onSaved?: () => void) => {
    resetSave();
    update({ id: prospect.id, request }, { onSuccess: onSaved });
  };
  const sheetError = (addError ?? createError)?.message;
  // A blank employer or occupation, as the server read it from their FEC record — shown only, never saved.
  const work = row.workLine;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
      <div className="flex min-w-0 flex-col gap-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            {renderName(prospect.name, NAME_CLASS)}
            <p className="text-[13px] leading-5 text-muted-foreground">
              {joined(' · ', work.occupation.value, work.employer.value, joined(', ', prospect.city, prospect.state))}
            </p>
            {row.doNotCall && <p className="text-xs font-semibold text-destructive">Do Not Call — kept off every call book</p>}
          </div>
          <Button variant="outline" className="h-10" onClick={() => setAdding(true)}>
            Add to call book
          </Button>
        </div>
        {sheetError && <p className="text-sm text-destructive">{sheetError}</p>}

        <Money detail={detail} />

        <section aria-labelledby="timeline-heading" className="rounded-xl border bg-card p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 id="timeline-heading" className="text-base font-semibold">
              Timeline
            </h2>
            <span className="text-xs text-muted-foreground">Calls, follow-ups and commitments, newest first</span>
          </div>
          <ProspectTimeline items={detail.timeline} />
        </section>
      </div>

      <div className="flex flex-col gap-4">
        <Note prospect={prospect} />
        <Details prospect={prospect} work={work} onSave={save} isSaving={isSaving} saveError={saveError?.message ?? null} onRemoved={onRemoved} />
        <CallBooks prospectId={prospect.id} books={detail.callBooks} sheets={sheets} />
        <FederalRecords detail={detail} onSave={save} isSaving={isSaving} />
      </div>

      <AddToCallSheetDialog
        open={adding}
        onOpenChange={setAdding}
        selected={[row]}
        books={sheets}
        onAdd={(sheet, ids) => {
          resetAdd();
          addToSheet({ sheet, prospectIds: ids });
        }}
        onCreate={(name, ids) => {
          resetCreate();
          createSheet({ name, prospectIds: ids });
        }}
      />
    </div>
  );
}

/** The three kinds of money, each in its own look: the Ask (ink), committed (warning, not money yet), federal (regular). */
function Money({ detail }: { detail: Detail }) {
  const { row, openCommitment, federalGiftCount, firstFederalCycle } = detail;

  // Names their real largest gift, which the Ask may sit below on a race with a contribution limit —
  // so both numbers are in view, with the total beside them.
  const largestGift = row.federalGiving?.largestSingleGift;
  const askCaption =
    row.askBasis == null
      ? noAskCaption(row.matchStatus)
      : largestGift == null
        ? askBasisLabel(row.askBasis)
        : `${askBasisLabel(row.askBasis)}: ${dollars.format(largestGift)}`;
  const federalCaption = row.federalGiving
    ? joined(' ', `${plural(federalGiftCount, 'gift')} to others`, firstFederalCycle != null && `since ${firstFederalCycle}`)
    : noFederalCaption(row.matchStatus);

  return (
    <div className="grid grid-cols-1 rounded-xl border bg-card sm:grid-cols-3">
      <Figure label="Suggested ask" caption={askCaption}>
        <span className={cn('font-bold', row.ask == null && 'text-muted-foreground')}>{row.ask == null ? '—' : dollars.format(row.ask)}</span>
      </Figure>
      <Figure
        label="Committed"
        labelClass={openCommitment ? 'text-warning' : undefined}
        caption={
          openCommitment
            ? joined(' · ', 'Open', openCommitment.expectBy ? `expected ${shortDay(openCommitment.expectBy)}` : 'no date given', 'not money yet')
            : 'Nothing open'
        }
      >
        {openCommitment ? (
          <span className="font-bold text-warning">{dollars.format(openCommitment.amount)}</span>
        ) : (
          <span className="font-bold text-muted-foreground">—</span>
        )}
      </Figure>
      <Figure label="Federal giving" caption={federalCaption} last>
        {row.federalGiving ? (
          <span className="font-medium">{dollars.format(row.federalGiving.totalAcrossAcceptedIdentities)}</span>
        ) : (
          <span className="font-medium text-muted-foreground">—</span>
        )}
      </Figure>
    </div>
  );
}

function Figure({
  label,
  labelClass,
  caption,
  last = false,
  children,
}: {
  label: string;
  labelClass?: string;
  caption: string;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <div role="group" aria-label={label} className={cn('flex flex-col gap-1 px-5 py-4', !last && 'border-b sm:border-b-0 sm:border-r')}>
      <span className={cn('text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground', labelClass)}>{label}</span>
      <span className="text-[26px] leading-[30px] tracking-tight tabular-nums">{children}</span>
      <span className="text-[11px] leading-4 text-muted-foreground">{caption}</span>
    </div>
  );
}

function Card({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  const id = `prospect-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2.5 rounded-xl border bg-card px-5 py-4">
      <div className="flex items-center justify-between gap-2">
        <h2 id={id} className="text-base font-semibold">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Details({
  prospect,
  work,
  onSave,
  isSaving,
  saveError,
  onRemoved,
}: {
  prospect: ProspectDto;
  /** Their employer and occupation as shown — a blank filled from their FEC record. The editor keeps their own. */
  work: ProspectWorkLineDto;
  onSave: (request: UpdateProspectRequest, onSaved?: () => void) => void;
  isSaving: boolean;
  saveError: string | null;
  onRemoved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const facts: { label: string; value: string; fromFec: boolean }[] = [
    { label: 'Phone', value: formatPhone(prospect.phone) || '—', fromFec: false },
    { label: 'Email', value: prospect.email?.trim() || '—', fromFec: false },
    { label: 'Address', value: prospectAddress(prospect) || '—', fromFec: false },
    { label: 'Employer', value: work.employer.value?.trim() || '—', fromFec: work.employer.fromFec },
    { label: 'Occupation', value: work.occupation.value?.trim() || '—', fromFec: work.occupation.fromFec },
  ];
  return (
    <Card
      title="Details"
      action={
        !editing && (
          <Button variant="ghost" size="sm" className="h-10" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )
      }
    >
      {editing ? (
        <ProspectDetailsForm
          prospect={prospect}
          onSave={(request) => onSave(request, () => setEditing(false))}
          onCancel={() => setEditing(false)}
          isSaving={isSaving}
          error={saveError}
        />
      ) : (
        <>
          <dl className="flex flex-col gap-2.5">
            {facts.map(({ label, value, fromFec }) => (
              <div key={label} className="grid grid-cols-[90px_minmax(0,1fr)] gap-3 text-[13px] leading-5">
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="break-words tabular-nums">
                  {value}
                  {fromFec && <span className="block text-[11px] leading-4 text-muted-foreground">{FEC_WORK_LINE_CAPTION}</span>}
                </dd>
              </div>
            ))}
          </dl>
          {saveError && <p className="text-sm text-destructive">{saveError}</p>}
          <div>
            <ProspectRemove prospect={prospect} onRemoved={onRemoved} />
          </div>
        </>
      )}
    </Card>
  );
}

/** Their person-wide note — the same on every call book — which the Caller reads before dialling. */
function Note({ prospect }: { prospect: ProspectDto }) {
  const [editing, setEditing] = useState(false);
  const { mutate: saveNotes, isPending, error, reset } = useUpdateProspectNotes();
  const has = !!prospect.notes?.trim();
  return (
    <Card
      title="Note"
      action={
        !editing && (
          <Button variant="ghost" size="sm" className="h-10" onClick={() => setEditing(true)}>
            {has ? 'Edit note' : 'Add note'}
          </Button>
        )
      }
    >
      {editing ? (
        <ProspectNoteEditor
          note={prospect.notes}
          autoFocus
          isSaving={isPending}
          error={error?.message ?? null}
          onCancel={() => {
            reset();
            setEditing(false);
          }}
          onSave={(notes) => {
            reset();
            saveNotes(
              { id: prospect.id, notes, concurrencyVersion: prospect.concurrencyVersion },
              { onSuccess: () => setEditing(false) },
            );
          }}
        />
      ) : has ? (
        <p className="whitespace-pre-wrap break-words text-[13px] leading-5">{prospect.notes}</p>
      ) : (
        <p className="text-[13px] text-muted-foreground">No note yet. What should a caller know before dialling?</p>
      )}
    </Card>
  );
}

function CallBooks({ prospectId, books, sheets }: { prospectId: string; books: ProspectCallBookDto[]; sheets: CallSheetMembershipDto[] }) {
  const { mutate: removeFrom, error, reset } = useRemoveFromCallSheet();
  return (
    <Card title="Call books">
      {books.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">Not on any call book yet.</p>
      ) : (
        <ul className="flex flex-col">
          {books.map((book) => (
            <li key={book.id} className="flex min-h-11 items-center gap-3 border-t">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[13px] font-semibold">{book.name}</span>
                <span className="text-[11px] leading-4 tabular-nums text-muted-foreground">
                  #{count.format(book.position)} of {count.format(book.size)} ·{' '}
                  {book.lastCalledOn ? `called ${shortDay(book.lastCalledOn)}` : 'not called from it yet'}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-10"
                aria-label={`Remove from ${book.name}`}
                onClick={() => {
                  reset();
                  // The book read's membership carries the freshest token; the detail's is the fallback.
                  const sheet = sheets.find((s) => s.id === book.id) ?? {
                    id: book.id,
                    name: book.name,
                    size: book.size,
                    concurrencyVersion: book.concurrencyVersion,
                    prospectIds: [prospectId],
                  };
                  removeFrom({ sheet, prospectIds: [prospectId] });
                }}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-sm text-destructive">{error.message}</p>}
    </Card>
  );
}

function FederalRecords({
  detail,
  onSave,
  isSaving,
}: {
  detail: Detail;
  onSave: (request: UpdateProspectRequest) => void;
  isSaving: boolean;
}) {
  const { row, records } = detail;
  const { prospect } = row;
  const [unlinking, setUnlinking] = useState<{ record: AcceptedRecordDto; letter: string } | null>(null);
  const { mutate: decide, isPending: isDeciding, error: decideError, reset } = useRecordMatchVerdict();
  const open = row.possibleMatchCount;

  return (
    <Card
      title="Federal records"
      action={
        records.length > 0 && (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-success">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-success" />
            {count.format(records.length)} matched
          </span>
        )
      }
    >
      {open > 0 && (
        <Link
          to="/admin/fundraising/match-review"
          search={{ prospect: prospect.id }}
          className="flex min-h-11 items-center gap-2 rounded-lg border border-warning/40 bg-warning/5 px-3 text-[13px] font-semibold text-warning hover:underline"
        >
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-warning" />
          {plural(open, 'possible record')} to answer in Match review
        </Link>
      )}
      {records.length === 0 && open === 0 && <p className="text-[13px] text-muted-foreground">No federal records are theirs yet.</p>}
      {records.map((accepted, index) => {
        const { record } = accepted;
        const letter = letterOf(index);
        return (
          <div key={candidateKey(record)} role="group" aria-label={`Record ${letter}`} className="flex flex-col gap-2 rounded-[10px] border px-3.5 py-3">
            <div className="flex items-center justify-between gap-2">
              <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Record {letter}</span>
              <span className="text-[11px] text-muted-foreground">
                {accepted.decidedOn
                  ? joined(' · ', `Confirmed by ${accepted.decidedBy ?? 'someone no longer on the campaign'}`, shortDay(accepted.decidedOn))
                  : 'Matched automatically'}
              </span>
            </div>
            <RecordField
              label="Address"
              value={recordAddress(record)}
              inUse={addressInUse(record, prospect)}
              action="Use address"
              disabled={isSaving}
              onUse={() => onSave(addressFromRecord(prospect, record))}
            />
            {recordWork(record) && (
              <RecordField
                label="Employer · occupation"
                value={recordWork(record)}
                inUse={workInUse(record)}
                action="Use this"
                disabled={isSaving}
                onUse={() => onSave(workFromRecord(prospect, record))}
              />
            )}
            <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
              {record.giving.map((g) => (
                <li key={`${g.committee}-${g.cycle}`} className="tabular-nums">
                  {joined(' · ', g.committee, String(g.cycle), plural(g.giftCount, 'gift'), dollars.format(g.total))}
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between gap-2 border-t pt-2">
              <span className="text-xs tabular-nums">
                {plural(record.giftCount, 'gift')} · {dollars.format(record.totalFederal)} federal
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-10"
                disabled={isDeciding}
                onClick={() => {
                  reset();
                  setUnlinking({ record: accepted, letter });
                }}
              >
                Unlink
              </Button>
            </div>
          </div>
        );
      })}
      {decideError && <p className="text-sm text-destructive">{decideError.message}</p>}

      <AlertDialog open={unlinking !== null} onOpenChange={(next) => !next && setUnlinking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink record {unlinking?.letter}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  It stops counting as {prospect.name}’s giving, and you won’t be asked about it again.{' '}
                  {unlinking?.record.askIfUnlinked != null
                    ? `The suggested ask becomes ${dollars.format(unlinking.record.askIfUnlinked)}.`
                    : 'There’ll be no suggested ask until another federal record is theirs.'}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (unlinking) decide({ prospectId: prospect.id, request: unlinkVerdict(unlinking.record) }, { onSettled: () => setUnlinking(null) });
              }}
            >
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function RecordField({
  label,
  value,
  inUse,
  action,
  disabled,
  onUse,
}: {
  label: string;
  value: string;
  inUse: boolean;
  action: string;
  disabled: boolean;
  onUse: () => void;
}) {
  return (
    <div className="grid min-h-8 grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
      <span className="flex min-w-0 flex-col">
        <span className="text-[11px] text-muted-foreground">{label}</span>
        <span className="text-[13px] leading-[18px]">{value}</span>
      </span>
      {inUse ? (
        <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">In use</span>
      ) : (
        <Button variant="link" size="sm" className="h-10 px-0 text-xs font-semibold" disabled={disabled} onClick={onUse}>
          {action}
        </Button>
      )}
    </div>
  );
}
